import React, { ReactNode } from "react";
import { getIn, useFormikContext } from "formik";
import { WhmTextField, WhmTextFieldProps } from "@securitize/reactjs-whm";
import { REQUIRED_GENERIC_HIDDEN_MESSAGE } from "../../utils/yupUtils";

interface FormikWhmTextFieldProps extends Omit<WhmTextFieldProps, "name"> {
  name: string;
  dataTestId?: string;
  autoTrim?: boolean;
}

export default function FormikWhmTextField<FormikFormType>({
  name,
  helperText: customHelperText,
  error: customError,
  onBlur: customOnBlur,
  onChange: customOnChange,
  value,
  dataTestId,
  autoTrim,
  ...rest
}: FormikWhmTextFieldProps) {
  const formik = useFormikContext<FormikFormType>();

  const defaultValue = getIn(formik.values, name) as unknown;
  const errorMessage = (getIn(formik.touched, name) &&
    getIn(formik.errors, name)) as ReactNode;

  const hasError = !!errorMessage;
  const defaultHelperText =
    errorMessage === REQUIRED_GENERIC_HIDDEN_MESSAGE ? "" : errorMessage;

  return (
    <WhmTextField
      name={name}
      value={value ?? defaultValue}
      onChange={customOnChange ?? formik.handleChange}
      onBlur={(e) => {
        if (autoTrim && e.target.value) {
          const trimmedValue = e.target.value.trim();
          void formik.setFieldValue(name, trimmedValue);
        }
        if (customOnBlur) {
          customOnBlur(e);
        } else {
          formik.handleBlur(e);
        }
      }}
      error={customError ?? hasError}
      helperText={customHelperText ?? defaultHelperText}
      inputProps={{
        "data-test-id": dataTestId ?? `formik-text-field-${name}`,
      }}
      {...rest}
    />
  );
}
